import { mapGetters } from "vuex";

import IntroPack from "./components/IntroPack";
import Interview from "./components/Interview";
import EventRegistration from "./components/EventRegistration";
import Meeting from "./components/Meeting";
import ProjectProfile from "./components/ProjectProfile";
import AtsCandidate from "./components/AtsCandidate";
import PoolCandidateAssoc from "./components/PoolCandidateAssoc";
import Conversation from "./components/Conversation";
import Task from "./components/Task";
import NoResults from "@/components/NoResults";
import RouteContext from "@/common/RouteContext";
import ThreadRequiresUserAttentionIcon from "@/components/ThreadRequiresUserAttentionIcon";
import UpdateStatusButton from "@/components/candidates/UpdateStatusButton/UpdateStatusButton";
import CandidateStatusDiagram from "@/components/CandidateStatusDiagram/Component";

export default {
  name: "CandidateSidebarOutreachTab",
  components: {
    AtsCandidate,
    CandidateStatusDiagram,
    Conversation,
    EventRegistration,
    Interview,
    IntroPack,
    Meeting,
    NoResults,
    PoolCandidateAssoc,
    ProjectProfile,
    Task,
    ThreadRequiresUserAttentionIcon,
    UpdateStatusButton,
  },
  data() {
    return {
      currentTouchpoint: null,
      routeContext: new RouteContext(this.$route.name),
      showAllJobs: false,
    };
  },
  computed: {
    ...mapGetters([
      "currentCandidate",
      "currentCandidateTouchpoints",
      "currentProject",
      "currentUser",
      "isLoadingCurrentCandidateHistory",
    ]),
    showHistory() {
      // Drive the empty-state message off the unfiltered list so toggling
      // the per-job filter on a candidate who has events elsewhere doesn't
      // wrongly show "There is no outreach history".
      return !this.isLoadingCurrentCandidateHistory && this.allTouchpoints.length > 0;
    },
    allTouchpoints() {
      if (this.isLoadingCurrentCandidateHistory) {
        return [];
      }
      return this.currentCandidateTouchpoints;
    },
    currentProjectId() {
      return this.currentProject?.id || null;
    },
    hasProjectContext() {
      return !!this.currentProjectId;
    },
    // Profile-level touchpoint types — no project_id — so they're always shown
    // regardless of the per-job filter. Humanly types (Conversation, Interview,
    // Event, Meeting) plus talent-community membership (PoolCandidateAssoc,
    // emitted as either short name or fully-qualified Ruby class).
    profileLevelTouchpointTypes() {
      return [
        "Conversation",
        "Interview",
        "Event",
        "Meeting",
        "PoolCandidateAssoc",
        "Humanly::Candidates::PoolCandidateAssoc",
      ];
    },
    touchpointsList() {
      if (!this.hasProjectContext || this.showAllJobs) {
        return this.allTouchpoints;
      }
      return this.allTouchpoints.filter(tp =>
        this.profileLevelTouchpointTypes.includes(tp.touchpointType) ||
        tp.projectId === this.currentProjectId
      );
    },
    showFilteredEmptyState() {
      // showHistory is true (the candidate has events) but the per-job filter
      // hides them all — explain why instead of looking like there's nothing.
      return this.showHistory &&
        this.hasProjectContext &&
        !this.showAllJobs &&
        this.touchpointsList.length === 0;
    },
    isTouchpointTask() {
      return this.currentTouchpoint?.touchpointType === "Task";
    },
    isTouchpointIntroPack() {
      return this.currentTouchpoint?.touchpointType === "IntroPack";
    },
    isTouchpointAtsCandidate() {
      return this.currentTouchpoint?.touchpointType === "AtsCandidate";
    },
    isTouchpointProjectProfile() {
      return this.currentTouchpoint?.touchpointType === "ProjectProfile";
    },
    isTouchpointConversation() {
      return this.currentTouchpoint?.touchpointType === "Conversation";
    },
    isTouchpointInterview() {
      return this.currentTouchpoint?.touchpointType === "Interview";
    },
    isTouchpointPoolCandidateAssoc() {
      return this.currentTouchpoint?.touchpointType === "PoolCandidateAssoc";
    },
    isTouchpointEvent() {
      return this.currentTouchpoint?.touchpointType === "Event";
    },
    isTouchpointMeeting() {
      return this.currentTouchpoint?.touchpointType === "Meeting";
    },
  },
  watch: {
    isLoadingCurrentCandidateHistory: {
      immediate: true,
      handler(isLoading) {
        // Use the unfiltered list here so a deep-linked touchpoint from
        // another job isn't dropped by the current-job filter before we get
        // a chance to flip it off below.
        if (isLoading || (!isLoading && !this.allTouchpoints.length)) {
          return;
        }

        let touchpoint = null;
        if (this.$route.query.outreachId) {
          touchpoint = this.currentCandidate.tasks.find(t => t.id.toString() === this.$route.query.outreachId);
        } else if (this.$route.query.referral) {
          touchpoint = this.currentCandidate.introPacks.find(ip => ip.id.toString() === this.$route.query.referral);
        } else if (this.$route.query.sflId) {
          touchpoint = this.currentCandidate.savedForLaters.find(sfl => sfl.id.toString() === this.$route.query.sflId);
        } else if (this.$route.query.acId) {
          touchpoint = this.currentCandidate.atsCandidates.find(ac => ac.id.toString() === this.$route.query.acId);
        } else {
          touchpoint = this.touchpointsList[0];
        }

        this.maybeShowAllJobsFor(touchpoint);
        this.selectTouchpoint(touchpoint);
      },
    },
    "$route.query.outreachId"() {
      if (this.$route.query.outreachId) {
        const task = this.currentCandidate.tasks?.find(t => t.id.toString() === this.$route.query.outreachId);
        if (task) {
          this.maybeShowAllJobsFor(task);
          this.currentTouchpoint = task;
        }
      }
    },
    "$route.query.acId"() {
      if (this.$route.query.acId) {
        const atsCandidate = this.currentCandidate.atsCandidates?.find(t => t.id.toString() === this.$route.query.acId);
        if (atsCandidate) {
          this.maybeShowAllJobsFor(atsCandidate);
          this.currentTouchpoint = atsCandidate;
        }
      }
    },
    "$route.query.referral"() {
      if (this.$route.query.referral) {
        const ip = this.currentCandidate.introPacks.find(t => t.id.toString() === this.$route.query.referral);
        if (ip) {
          this.maybeShowAllJobsFor(ip);
          this.currentTouchpoint = ip;
        }
      }
    },
    "$route.query.sflId"() {
      if (this.$route.query.sflId) {
        const sfl = this.currentCandidate.savedForLaters.find(t => t.id.toString() === this.$route.query.sflId);
        if (sfl) {
          this.maybeShowAllJobsFor(sfl);
          this.currentTouchpoint = sfl;
        }
      }
    },
  },
  methods: {
    // When a deep-link selects a touchpoint that's in a different project
    // than the current one, drop the per-job filter so the timeline still
    // shows it (and its surrounding context).
    maybeShowAllJobsFor(touchpoint) {
      if (!touchpoint || !this.hasProjectContext) {
        return;
      }
      if (this.profileLevelTouchpointTypes.includes(touchpoint.touchpointType)) {
        return;
      }
      if (touchpoint.projectId && touchpoint.projectId !== this.currentProjectId) {
        this.showAllJobs = true;
      }
    },
    closeTouchpoint() {
      this.selectTouchpoint(null);
    },
    viewTouchpoint({ id, type }) {
      let tp = null;

      if (type === "IntroPack") {
        tp = this.currentCandidate.introPacks.find(t => t.id === id);
      } else if (type === "Task") {
        tp = this.currentCandidate.tasks.find(t => t.id === id);
      } else if (type === "ProjectProfile") {
        tp = this.currentCandidate.savedForLaters.find(t => t.id === id);
      } else if (type === "AtsCandidate") {
        tp = this.currentCandidate.atsCandidates.find(t => t.id === id);
      } else if (type === "PoolCandidateAssoc" || type === "Humanly::Candidates::PoolCandidateAssoc") {
        tp = this.currentCandidate.poolCandidateAssocs?.find(t => t.id === id);
      } else if (type === "Conversation") {
        tp = this.currentCandidate.humanlyProfile?.conversations?.find(t => t.id === id);
      } else if (type === "Interview") {
        tp = this.currentCandidate.humanlyProfile?.interviews?.find(t => t.id === id);
      } else if (type === "Event") {
        tp = this.currentCandidate.humanlyProfile?.events?.find(t => t.id === id);
      } else if (type === "Meeting") {
        tp = this.currentCandidate.humanlyProfile?.meetings?.find(t => t.id === id);
      }


      this.selectTouchpoint(tp);
    },
    campaignNameLabel(touchpoint) {
      if (touchpoint.touchpointType === "PoolCandidateAssoc") {
        return "Talent Community";
      } else if (touchpoint.touchpointType === "Conversation") {
        return "Humanly text Screening";
      } else if (touchpoint.touchpointType === "Interview") {
        return "Humanly Interview Evaluation";
      } else if (touchpoint.touchpointType === "Event") {
        return "Event Registration";
      } else if (touchpoint.touchpointType === "Meeting") {
        return "Scheduled Meeting";
      } else if (touchpoint.campaign) {
        return touchpoint.campaign.name;
      } else if (touchpoint.sequence) {
        return touchpoint.sequence.name;
      } else if (touchpoint.touchpointType === "AtsCandidate") {
        return `Applied to ${touchpoint.projectName}`;
      } else {
        return "Manual Outreach";
      }
    },
    touchpointStatusKey(touchpoint) {
      if (touchpoint.activity.isNegative && touchpoint.activity.statusKey === "sourced") {
        return "sourced-stopped";
      }
      return touchpoint.activity.statusKey;
    },
    touchpointStatusLabel(touchpoint) {
      let status = touchpoint.activity.name;
      if (touchpoint.activity.isNegative) {
        if (status === "Sourced") {
          status = "Stopped";
        }
      }
      if (touchpoint.activity.wasOverridden) {
        status = `${status}*`;
      }

      return status;
    },
    selectTouchpoint(touchpoint) {
      let query = Object.assign({}, this.$route.query);

      delete query.referral;
      delete query.outreachId;
      delete query.sflId;
      delete query.acId;

      if (touchpoint?.touchpointType === "Task") {
        if (this.$route.query.outreachId !== touchpoint.id.toString()) {
          query.outreachId = touchpoint.id;
        }
      } else if (touchpoint?.touchpointType === "IntroPack") {
        if (this.$route.query.referral !== touchpoint.id.toString()) {
          query.referral = touchpoint.id;
        }
      } else if (touchpoint?.touchpointType === "ProjectProfile") {
        if (this.$route.query.sflId !== touchpoint.id.toString()) {
          query.sflId = touchpoint.id;
        }
      } else if (touchpoint?.touchpointType === "AtsCandidate") {
        if (this.$route.query.acId !== touchpoint.id.toString()) {
          query.acId = touchpoint.id;
        }
      }
      this.$router.replace({ query });
      this.currentTouchpoint = touchpoint;
    },
  },
};
